import React, {FC, useContext, useEffect, useState} from "react";
import {Button, Form, Modal, Table} from "react-bootstrap";
import {AuthContext} from "contexts";
import {adminsService, AdminUser,} from "services/adminsService";
import {Loader} from "components/Loader";
import {AppNames, isModerationPackAvailable} from "enums";
import {AdminsRoles} from "enums/AdminsRoles";
import {
    ADMIN_CONFIGURABLE_APP_IDS,
    AppEnvsState,
    appLinksToEnvsState,
    mergeAppLinksForSave,
} from "pages/Admins/adminAppLinksUtils";
import {RoleSelect} from "./RoleSelect";
import toast from "react-hot-toast";

type Props = {
    admin: AdminUser | null;
    show: boolean;
    onHide: () => void;
    onSaved: () => void;
};

export const EditAdminAccessModal: FC<Props> = ({admin, show, onHide, onSaved}) => {
    const {env} = useContext(AuthContext);
    const [appEnvs, setAppEnvs] = useState<AppEnvsState>({});
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [role, setRole] = useState<AdminsRoles>(AdminsRoles.None);
    const [name, setName] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!show || !admin) {
            return;
        }
        setAppEnvs(appLinksToEnvsState(admin.appLinks));
        setIsSuperAdmin(admin.isSuperAdmin);
        setName(admin.name);
        const currentRole = admin.role
            ? admin.role
            : admin.isSuperAdmin
                ? AdminsRoles.SuperAdmin
                : AdminsRoles.None;
        setRole(currentRole);
    }, [show, admin]);

    const toggleEnv = (appId: number, key: "prod" | "test") => {
        setAppEnvs((prev) => {
            const cur = prev[appId] ?? {prod: false, test: false};
            return {
                ...prev,
                [appId]: {
                    ...cur,
                    [key]: !cur[key],
                },
            };
        });
    };

    const handleSave = async () => {
        if (!env || !admin) {
            return;
        }
        const appLinks = mergeAppLinksForSave(appEnvs, admin.appLinks);
        const showSuperAdmin = isModerationPackAvailable(env);
        const resolvedSuperAdmin = showSuperAdmin
            ? isSuperAdmin
            : role === AdminsRoles.SuperAdmin;
        setSaving(true);
        try {
            await adminsService.update(admin.userKey, {
                appLinks,
                isSuperAdmin: resolvedSuperAdmin,
                role,
                name: name.trim(),
            });
            toast.success("Сохранено");
            onSaved();
            onHide();
        } catch {
            /* request() already toasts details when withError */
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!saving) {
            onHide();
        }
    };

    const showSuperAdmin = isModerationPackAvailable(env);

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Редактирование админа</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Имя</Form.Label>
                        <Form.Control
                            placeholder="Имя"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Роль</Form.Label>
                        <RoleSelect value={role} onChange={setRole} allowNone={false}/>
                    </Form.Group>
                    <label className="fw-semibold d-block mb-2">Приложения и среды</label>
                    <Table size="sm" bordered responsive className="mb-3">
                        <thead>
                        <tr>
                            <th>Приложение</th>
                            <th className="text-center">Prod</th>
                            <th className="text-center">Test</th>
                        </tr>
                        </thead>
                        <tbody>
                        {ADMIN_CONFIGURABLE_APP_IDS.map((appId) => (
                            <tr key={appId}>
                                <td className="align-middle">{AppNames[appId] ?? appId}</td>
                                <td className="text-center align-middle">
                                    <Form.Check
                                        type="checkbox"
                                        checked={appEnvs[appId]?.prod ?? false}
                                        onChange={() => toggleEnv(appId, "prod")}
                                        aria-label={`${AppNames[appId]} Prod`}
                                    />
                                </td>
                                <td className="text-center align-middle">
                                    <Form.Check
                                        type="checkbox"
                                        checked={appEnvs[appId]?.test ?? false}
                                        onChange={() => toggleEnv(appId, "test")}
                                        aria-label={`${AppNames[appId]} Test`}
                                    />
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={saving}>
                    Отмена
                </Button>
                <Button
                    variant="primary"
                    onClick={() => void handleSave()}
                    disabled={saving || !env || !admin || !name.trim() || role === AdminsRoles.None}
                >
                    <Loader isLoading={saving}/> Сохранить
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
